import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { BookingClosedPage } from '../../components/notices/BookingClosedNotice';
import { extractError } from '../../utils';

const registerSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  studentId: z.string().trim().min(1, 'Matric number is required'),
  department: z.string().trim().min(1, 'Department is required'),
  // Decides which hostel sections this student may book, so it is asked for
  // up front rather than discovered when a booking is refused.
  gender: z.enum(['MALE', 'FEMALE'], { message: 'Select male or female' }),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required').min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: registerUser } = useAuth();
  const { bookingWindow, loading: brandLoading } = useBrand();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const redirectParam = searchParams.get('redirect');

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      studentId: '',
      department: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  // Handle browser autofill detection
  useEffect(() => {
    const checkAutofill = () => {
      const firstNameInput = document.querySelector('input[name="firstName"]') as HTMLInputElement;
      const lastNameInput = document.querySelector('input[name="lastName"]') as HTMLInputElement;
      const studentIdInput = document.querySelector('input[name="studentId"]') as HTMLInputElement;
      const departmentInput = document.querySelector('input[name="department"]') as HTMLInputElement;
      const emailInput = document.querySelector('input[name="email"]') as HTMLInputElement;
      const passwordInput = document.querySelector('input[name="password"]') as HTMLInputElement;
      const confirmPasswordInput = document.querySelector('input[name="confirmPassword"]') as HTMLInputElement;
      
      // Only set values if they're autofilled and the form field is still empty
      if (firstNameInput?.value) {
        setValue('firstName', firstNameInput.value, { shouldValidate: false, shouldTouch: false });
      }
      
      if (lastNameInput?.value) {
        setValue('lastName', lastNameInput.value, { shouldValidate: false, shouldTouch: false });
      }

      if (studentIdInput?.value) {
        setValue('studentId', studentIdInput.value, { shouldValidate: false, shouldTouch: false });
      }

      if (departmentInput?.value) {
        setValue('department', departmentInput.value, { shouldValidate: false, shouldTouch: false });
      }
      
      if (emailInput?.value) {
        setValue('email', emailInput.value, { shouldValidate: false, shouldTouch: false });
      }
      
      if (passwordInput?.value) {
        setValue('password', passwordInput.value, { shouldValidate: false, shouldTouch: false });
      }
      
      if (confirmPasswordInput?.value) {
        setValue('confirmPassword', confirmPasswordInput.value, { shouldValidate: false, shouldTouch: false });
      }
    };

    // Check immediately and after a delay for autofill
    checkAutofill();
    const timer = setTimeout(checkAutofill, 200);

    return () => clearTimeout(timer);
  }, [setValue]);

  const resolveRedirect = (redirect: string | null) => {
    if (!redirect) {
      return '/student/dashboard';
    }

    try {
      const decoded = decodeURIComponent(redirect);
      return decoded.startsWith('/') ? decoded : '/student/dashboard';
    } catch (_error) {
      return '/student/dashboard';
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { confirmPassword, ...registerData } = data;
      await registerUser(registerData);
      navigate(resolveRedirect(redirectParam));
    } catch (err: any) {
      setError(extractError(err, 'Registration failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  // The booking window defaults to closed until the settings endpoint answers,
  // so wait for it rather than flashing the closed page at every visitor.
  if (brandLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary px-4 py-12">
        <LoadingSpinner size="lg" label="Loading registration" />
      </div>
    );
  }

  // The route stays mounted rather than redirecting, so anyone following an
  // old signup link lands on an explanation with a route to support instead
  // of a silent bounce to the home page.
  if (!bookingWindow.registrationOpen) {
    return <BookingClosedPage headline="Registration is Closed" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Student booking and payment portal
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                autoComplete="given-name"
                {...register('firstName')}
                error={touchedFields.firstName ? errors.firstName?.message : undefined}
              />
              <Input
                label="Last Name"
                type="text"
                autoComplete="family-name"
                {...register('lastName')}
                error={touchedFields.lastName ? errors.lastName?.message : undefined}
              />
            </div>
            
            <div className="space-y-2">
              <Input
                label="Matric Number"
                type="text"
                autoComplete="off"
                placeholder="e.g. CSC/2024/001"
                {...register('studentId')}
                error={touchedFields.studentId ? errors.studentId?.message : undefined}
              />
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-warning-subtle p-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                <p className="text-xs text-warning-foreground">
                  Please double-check your matric number before continuing. It is
                  used to verify your student record, and an incorrect matric
                  number can delay or invalidate your booking.
                </p>
              </div>
            </div>

            <Input
              label="Department"
              type="text"
              autoComplete="organization-title"
              placeholder="e.g. Computer Science"
              {...register('department')}
              error={touchedFields.department ? errors.department?.message : undefined}
            />

            <div>
              <label className="form-label">Gender</label>
              <div className="grid grid-cols-2 gap-3">
                {(['MALE', 'FEMALE'] as const).map((value) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-secondary has-[:checked]:border-primary has-[:checked]:bg-brand-subtle"
                  >
                    <input type="radio" value={value} className="h-4 w-4" {...register('gender')} />
                    {value === 'MALE' ? 'Male' : 'Female'}
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Hostel sections are allocated to male or female students, so this decides which beds you can book.
              </p>
              {errors.gender?.message ? (
                <p className="mt-1 text-xs text-destructive">{errors.gender.message}</p>
              ) : null}
            </div>

            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              {...register('email')}
              error={touchedFields.email ? errors.email?.message : undefined}
            />
            
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              {...register('password')}
              error={touchedFields.password ? errors.password?.message : undefined}
            />
            
            <Input
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
              error={touchedFields.confirmPassword ? errors.confirmPassword?.message : undefined}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-destructive" />
              <div className="space-y-1 text-left">
                <p className="text-sm font-semibold text-destructive">
                  {error.toLowerCase().includes('rejected') || error.toLowerCase().includes('flagged')
                    ? 'Registration Rejected'
                    : 'Registration Failed'}
                </p>
                <p className="text-sm text-destructive/90">{error}</p>
                {(error.toLowerCase().includes('rejected') || error.toLowerCase().includes('flagged')) && (
                  <p className="text-xs text-muted-foreground pt-1">
                    If you believe this restriction is an error, please contact hostel administration with your matriculation credentials.
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <Button
              type="submit"
              loading={isLoading}
              className="w-full"
              size="lg"
            >
              Create Account
            </Button>
          </div>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                to={redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : '/login'}
                className="font-medium text-brand hover:text-brand-light"
              >
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
